import * as React from 'react'
import { Text } from '@amzn/dvui/dist/Text'
import * as st from './Restrictions.module.scss'
import { TextBlock } from '@amzn/dvui/dist/TextBlock'
import * as Types from 'DVWebNodeWpsRuntime'
import { ClientsideLogger } from '@utils'
import { ErrorAlert } from '@amzn/dvui/dist/Alert'

/*
 * PIN on Download based on the Web Player's PIN on Playback implementation 
 * https://code.amazon.com/packages/ATVWebCascadesPlayer/blobs/mainline/--/src/Modules/Views/Modals/PINModal.tsx
 */


const logger = new ClientsideLogger('DVWebNodeSPA/Restrictions')

/*
 * The key code returned by software keyboard on Android devices is always 229.
 * Please check https://bugs.chromium.org/p/chromium/issues/detail?id=118639 for more details.
 */
const ANDROID_DEFAULT_KEY_CODE = 229
const BULLET_CHARACTER = "\u2022"
/**
 * Provides numerical code identifying the unmodified value of the pressed key on keyboard.
 */
const KeyCode = {
  BACKSPACE: 8,
  DELETE: 46,
  ESC: 27,
  SHIFT: 16,
  SPACE: 32,
  TAB: 9,

  LEFT_ARROW: 37,
  UP_ARROW: 38,
  RIGHT_ARROW: 39,
  DOWN_ARROW: 40,

  START_OF_NUMBER: 48,
  END_OF_NUMBER: 57,
  START_OF_NUMPAD: 96,
  END_OF_NUMPAD: 105,
}

const isPinSet = (char: string) => !!char

export type InputArgs = {pin: string, isSubmitted: boolean}
export type OnPinSubmitArgs = {
  input: InputArgs
  submitEndpoint: string
}
export type OnPinSubmit = (args?: OnPinSubmitArgs) => void
type PinModalContentProps = Types.atv.wps.PinModalContentProps & {
  onPinSubmit: OnPinSubmit
  onPinCancel: () => void
}
interface InputProps {
  key: string | number;
  maxLength: number;
  onKeyDown: (event: React.KeyboardEvent<Element>) => void;
  onKeyUp: (event: React.KeyboardEvent<Element>) => void;
  onChange: (event: React.ChangeEvent) => void;
  type: string;
  value: string;
  ref?: (ref: HTMLInputElement) => void;
}
type KeyUpAction = {
  shouldAct: (keyCode: number) => boolean
  act: (keyCode: number) => void
}
type PinValues = string[]
type PinModalContentState = {
  pinFieldIndex: number
  pinValues: PinValues
  showIncorrectMessage: boolean
  enableMessage: boolean
  isSubmitting: boolean
}

// Using PureComponent to shallow compare props 
// so parent component rerender (due to download status) won't cause a form reset
export class PinModalContent extends React.PureComponent<PinModalContentProps, PinModalContentState> {
  state = {
    pinFieldIndex: 0,
    pinValues: [] as PinValues,
    showIncorrectMessage: false,
    enableMessage: true,
    isSubmitting: false,
  }

  /*
   * These will be asked, in order, if they shouldAct each time we get a keyup event on an input field.
   * Only the first action to return shouldAct === true will be executed.
   */
  private readonly keyUpActions: KeyUpAction[] = [
    {
      // Delete or Backspace key
      shouldAct: (keyCode: number) => keyCode === KeyCode.DELETE || keyCode === KeyCode.BACKSPACE,
      act: (_keyCode: number) => {
        let deletionIndex = this.state.pinFieldIndex

        if (deletionIndex > 0 && this.state.pinValues[deletionIndex] === '') {
          deletionIndex -= 1
          this.setState({
            pinFieldIndex: deletionIndex,
          })
        }

        this.updatePinValueAtIndex('', deletionIndex)
      },
    },
    {
      // Number key
      shouldAct: (keyCode: number) => this.keyCodeIsNumber(keyCode),
      act: (keyCode: number) => {
        const pinFieldIndex = this.state.pinFieldIndex
        let pinValue: number = keyCode
        pinValue -=
          KeyCode.START_OF_NUMBER <= keyCode && keyCode <= KeyCode.END_OF_NUMBER
            ? KeyCode.START_OF_NUMBER
            : KeyCode.START_OF_NUMPAD

        this.updatePinValueAtIndex(pinValue.toString(), pinFieldIndex, () => {
          if (pinFieldIndex !== this.props.pinLength - 1) {
            this.setState({
              pinFieldIndex: pinFieldIndex + 1,
              enableMessage: false, // Hide the previous message once started to type a new PIN
            })
          } else {
            this.submitPin()
          }
        })
      },
    },
    {
      // Esc key
      shouldAct: (keyCode: number) => keyCode === KeyCode.ESC,
      act: (_keyCode: number) => this.onPinCancel(),
    },
    {
      // Tab key
      shouldAct: (keyCode: number) => keyCode !== KeyCode.TAB && keyCode !== KeyCode.SHIFT,
      act: (_keyCode: number) => this.updatePinValueAtIndex('', this.state.pinFieldIndex),
    },
  ]

  updatePinValueAtIndex = (value: string, index: number, onComplete?: () => void) => {
    const newPinValues = this.state.pinValues.slice(0)
    newPinValues.splice(index, 1, value)

    this.setState(
      {
        pinValues: newPinValues,
      },
      onComplete
    )
  }

  submitPin = () => {
    const {onPinSubmit, submitEndpoint} = this.props
    const pin = this.state.pinValues.join('')
    logger.debug('submitPin', pin, submitEndpoint)
    this.lock()
    onPinSubmit({input: {pin, isSubmitted: true}, submitEndpoint})
  }

  lock = () => {
    this.setState({isSubmitting: true})
  }

  reset = () => {
    this.setState({
      enableMessage: true,
      isSubmitting: false,
      pinValues: [],
    })
    this.setFocus()
  }

  onPinCancel = () => {
    const {onPinCancel} = this.props
    onPinCancel()
  }

  private preventDefault(e: any) {
    if (e && e.preventDefault) {
      e.preventDefault()
    } else {
      e.returnValue = false
    }
  }

  private getKeyCode(e: React.KeyboardEvent<Element>): number {
    return window.event ? e.keyCode : e.which
  }

  private shouldFallbackToOnChange(keyCode: number): boolean {
    return !keyCode || keyCode === ANDROID_DEFAULT_KEY_CODE
  }

  private keyCodeIsNumber(keyCode: number): boolean {
    return (keyCode >= KeyCode.START_OF_NUMBER && keyCode <= KeyCode.END_OF_NUMBER)
      || (keyCode >= KeyCode.START_OF_NUMPAD && keyCode <= KeyCode.END_OF_NUMPAD)
  }

  private actOnKeyCode(keyCode: number): void {
    for (let potentialAction of this.keyUpActions) {
      if (potentialAction.shouldAct(keyCode)) {
        potentialAction.act(keyCode)
        return
      }
    }
  }

  /**
   * Used to make entering the PIN a better experience. Only allows the customer to enter numbers,
   * intelligently goes to the next PIN field or to the previous one, and allows the customer to
   * use the tab, shift+tab, and escape shortcuts.
   *
   * @param e
   */
  private onKeyUp = (e: React.KeyboardEvent<Element>) => {
    const keyCode: number = this.getKeyCode(e)
    if (!this.shouldFallbackToOnChange(keyCode)) {
      this.actOnKeyCode(keyCode)
    }
  }

  private onKeyDown = (e: React.KeyboardEvent<Element>) => {
    const keyCode: number = this.getKeyCode(e)
    if (!this.shouldFallbackToOnChange(keyCode)) {
      this.preventDefault(e)
    }
  }

  /**
   * Used as fallback when key pressing events are not working, e.g. on most of the Android devices.
   * It will be triggered only when preventDefault is not called during the onKeyDown event.
   * This onChange event cannot capture backspace and escape so it is only used as a fallback.
   *
   * @param e
   */
  private onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      const keyCode: number = e.target.value.charCodeAt(0)
      this.actOnKeyCode(keyCode)
    }
  }

  private setFocus = () => {
    this.setState({
        pinFieldIndex: 0
    })

    this.forceUpdate();
  }  

  componentDidMount() {
    this.setFocus()
  }

  componentDidUpdate(prevProps: PinModalContentProps) {
    if (prevProps !== this.props) {
      this.reset()
    }
  }

  render = () => {
    const {pinLength, description, helpInfo, message} = this.props
    const {pinValues, pinFieldIndex, enableMessage, isSubmitting} = this.state
    const showMessage = enableMessage && message && message.string

    return (
      <div className={`avu-text-context ${st.content}`}>
        <TextBlock>
          {showMessage && <ErrorAlert><Text string={message} tag={'p'} /></ErrorAlert>}
          {[...Array(pinLength)].map((_, i) => {
            const inputProps = {
              type: 'text',
              size: 1,
              maxLength: 1,
              className: st.pinInput,
              key: `pin-${i}`,
              onKeyDown: this.onKeyDown,
              onKeyUp: this.onKeyUp,
              onChange: this.onChange,
              value: isPinSet(pinValues[i]) ? BULLET_CHARACTER : '',
              disabled: isSubmitting,
            } as InputProps
            
            if (i === pinFieldIndex) {
              inputProps.ref = (ref) => {
                ref && ref.focus()
              }
            }

            return <input {...inputProps} />
          })}
          <Text className={[st.secondary, st.smallPrint].join(' ')} string={description} tag={'p'} />
          <Text className={st.smallPrint} string={helpInfo} tag={'p'} />
        </TextBlock>
      </div>
    )
  }
}
